import { useEffect, useState } from "react";

export type Platform = "mac" | "windows" | "linux" | "ios" | "android" | "unknown";
export type OperatingSystem = "mac" | "windows" | "linux" | "unknown";

export interface PlatformInfo {
  /** The detected platform */
  platform: Platform;
  /** The operating system (simplified for desktop) */
  os: OperatingSystem;
  /** Whether the platform is macOS */
  isMac: boolean;
  /** Whether the platform is Windows */
  isWindows: boolean;
  /** Whether the platform is Linux */
  isLinux: boolean;
  /** Whether the platform is iOS */
  isIOS: boolean;
  /** Whether the platform is Android */
  isAndroid: boolean;
  /** Whether the platform is mobile (iOS or Android) */
  isMobile: boolean;
  /** Whether the platform is desktop (Mac, Windows, or Linux) */
  isDesktop: boolean;
}

export interface UsePlatformDetectionOptions {
  /** Force a specific platform (useful for testing/stories) */
  forcePlatform?: Platform;
}

/**
 * Custom hook for detecting the user's platform and operating system
 *
 * @param options - Configuration options
 * @returns Platform information and helper booleans
 */
export function usePlatformDetection(options: UsePlatformDetectionOptions = {}): PlatformInfo {
  const { forcePlatform } = options;
  const [platformInfo, setPlatformInfo] = useState<PlatformInfo>(() => {
    // Initialize with default values to avoid hydration mismatches
    return createPlatformInfo("unknown");
  });

  useEffect(() => {
    if (forcePlatform) {
      setPlatformInfo(createPlatformInfo(forcePlatform));
      return;
    }

    const detectedPlatform = detectPlatform();
    setPlatformInfo(createPlatformInfo(detectedPlatform));
  }, [forcePlatform]);

  return platformInfo;
}

/**
 * Detects the current platform based on navigator information
 */
function detectPlatform(): Platform {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return "unknown";
  }

  const userAgent = navigator.userAgent.toLowerCase();

  // Check for mobile platforms first
  if (/iphone|ipod/.test(userAgent)) return "ios";
  if (/ipad/.test(userAgent)) return "ios";
  if (/android/.test(userAgent)) return "android";

  // Check for desktop platforms
  // Try modern userAgentData first
  if ("userAgentData" in navigator && (navigator as any).userAgentData?.platform) {
    const platform = (navigator as any).userAgentData.platform.toLowerCase();
    if (platform.includes("mac")) return "mac";
    if (platform.includes("win")) return "windows";
    if (platform.includes("linux")) return "linux";
  }

  // Fallback to userAgent
  if (/mac/.test(userAgent)) return "mac";
  if (/win/.test(userAgent)) return "windows";
  if (/linux/.test(userAgent)) return "linux";

  return "unknown";
}

/**
 * Creates a PlatformInfo object from a platform type
 */
function createPlatformInfo(platform: Platform): PlatformInfo {
  const isMac = platform === "mac";
  const isWindows = platform === "windows";
  const isLinux = platform === "linux";
  const isIOS = platform === "ios";
  const isAndroid = platform === "android";
  const isMobile = isIOS || isAndroid;
  const isDesktop = isMac || isWindows || isLinux;

  // Determine OS (simplified for desktop use cases)
  let os: OperatingSystem;
  if (isMac || isIOS) os = "mac";
  else if (isWindows) os = "windows";
  else if (isLinux || isAndroid) os = "linux";
  else os = "unknown";

  return {
    platform,
    os,
    isMac,
    isWindows,
    isLinux,
    isIOS,
    isAndroid,
    isMobile,
    isDesktop
  };
}

/**
 * Utility function for one-time platform detection (non-reactive)
 * Useful for cases where you don't need a hook
 */
export function getPlatformInfo(forcePlatform?: Platform): PlatformInfo {
  const platform = forcePlatform || detectPlatform();
  return createPlatformInfo(platform);
}
